import React from "react";
var __jsx = React.createElement;
import { Loader, Modal, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useAuth } from "@qure/qure-auth";
import { useSelectedPatientStore } from "@/stores/selected-patient";
import { StatusChip } from "@/components/status-chip";
import { MedicalReportModal, useSelectedDocument, getEncounterDocuments, getEncounterLabs } from "@/components/patient-history/records/encounters-panel";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { getAppointmentDateParts } from "./appointment-date-parts";
import { getEncounterStatusInfo } from "./get-encounter-status";
import { LabeledField } from "./labeled-field";
/** Encounter card with a date box, metadata grid, labs, clickable documents, and a status chip. */
export function EncounterItem(_ref) {
  var _userInfo$default_wor, _encounter$reason;
  var encounter = _ref.encounter;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useAuth = useAuth(),
    userInfo = _useAuth.userInfo;
  var workspaceId = (_userInfo$default_wor = userInfo === null || userInfo === void 0 ? void 0 : userInfo.default_workspace) !== null && _userInfo$default_wor !== void 0 ? _userInfo$default_wor : 0;
  var patientId = useSelectedPatientStore(function (s) {
    var _s$selectedPatient;
    return ((_s$selectedPatient = s.selectedPatient) === null || _s$selectedPatient === void 0 ? void 0 : _s$selectedPatient.patient_uid) != null ? Number(s.selectedPatient.patient_uid) : 0;
  });
  var _getAppointmentDatePa = getAppointmentDateParts(encounter.start_date),
    day = _getAppointmentDatePa.day,
    month = _getAppointmentDatePa.month,
    year = _getAppointmentDatePa.year,
    time = _getAppointmentDatePa.time;
  var reasonText = (_encounter$reason = encounter.reason) !== null && _encounter$reason !== void 0 && _encounter$reason.length ? encounter.reason.join(", ") : null;
  var labNames = getEncounterLabs(encounter);
  var labText = labNames.length ? labNames.join(", ") : null;
  var documents = getEncounterDocuments(encounter);
  var statusInfo = getEncounterStatusInfo(encounter.status);
  var _useSelectedDocument = useSelectedDocument(workspaceId, patientId),
    isOpening = _useSelectedDocument.isOpening,
    selectedDocument = _useSelectedDocument.selectedDocument,
    openDocument = _useSelectedDocument.openDocument,
    closeDocument = _useSelectedDocument.closeDocument;
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-4 flex-1 min-w-0 bg-sds-base-default rounded-sds-300 px-4 py-4 my-1 mr-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex gap-4 items-start w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col items-center justify-center shrink-0 self-stretch w-16 rounded-sds-200 border border-sds-base-default bg-sds-base-secondary text-center py-2 gap-0.5"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-heading-main text-sds-brand-default"
  }, day), /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-secondary"
  }, month, " ", year), /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-base text-sds-base-tertiary"
  }, time)), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-3 w-full min-w-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex gap-3 w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col w-full min-w-0"
  }, /*#__PURE__*/React.createElement("h3", {
    className: "text-style-body-strong text-sds-base-default text-left"
  }, [encounter.encounter_class, encounter.encounter_type].filter(Boolean).join(" | ") || "NA")), /*#__PURE__*/React.createElement(LabeledField, {
    label: t("label.provider"),
    value: encounter.provider
  }), /*#__PURE__*/React.createElement(LabeledField, {
    label: t("label.location"),
    value: encounter.location
  })))), /*#__PURE__*/React.createElement(LabeledField, {
    label: t("label.reason_for_exam"),
    value: reasonText
  }), /*#__PURE__*/React.createElement(LabeledField, {
    label: t("label.labs"),
    value: labText
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex items-start justify-between gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-1 min-w-0"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-tertiary"
  }, t("label.images")), documents.length > 0 ? documents.map(function (doc) {
    return /*#__PURE__*/React.createElement(UnstyledButton, {
      key: doc.resourceId,
      "data-testid": "encounter-document-".concat(doc.resourceId),
      onClick: function onClick() {
        trackEvent(ANALYTICS_EVENTS.ENCOUNTER_DOCUMENT, {
          patientId: patientId,
          recordId: doc.resourceId
        });
        openDocument(doc, encounter);
      },
      className: "text-style-label-base text-sds-brand-default underline decoration-sds-brand-default truncate text-left"
    }, doc.name);
  }) : /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small text-sds-base-tertiary"
  }, "NA")), /*#__PURE__*/React.createElement(StatusChip, {
    intent: statusInfo.intent,
    label: statusInfo.label
  })), isOpening && /*#__PURE__*/React.createElement(Modal, {
    opened: isOpening,
    onClose: function onClose() {},
    withCloseButton: false,
    centered: true,
    size: "sm",
    zIndex: 1300
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col items-center justify-center gap-4 py-6"
  }, /*#__PURE__*/React.createElement(Loader, {
    size: "md"
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-base text-sds-base-secondary"
  }, t("message.loading_image") || "Loading document…"))), selectedDocument && /*#__PURE__*/React.createElement(MedicalReportModal, {
    show: !!selectedDocument,
    onClose: closeDocument,
    document: selectedDocument
  }));
}